import {
  BusFuelInput,
  DEFAULT_TANK_CAPACITY_L,
  efficiencyScorePercent,
} from '../utils/busEfficiencyAnalyzer';

export type FuelEfficiencyResult = {
  pct: number | null;
  kmPerL: number | null;
  l100km: number | null;
  confidence: 'no_benchmark' | 'measured' | 'estimated' | 'early';
  confidenceLabel: string;
  tooltipNote: string;
};

export function estimateFuelEfficiency(input: BusFuelInput): FuelEfficiencyResult {
  const FILL_FRACTION = 0.8;
  const hasBenchmark = input.benchmarkKmPerL != null && input.benchmarkKmPerL > 0;
  const benchmark = input.benchmarkKmPerL ?? 0;
  const hasActualLitres =
    input.totalLitresAdded != null && input.totalLitresAdded > 0 && input.totalKm30 > 0;
  const actualKmPerL = hasActualLitres ? input.totalKm30 / input.totalLitresAdded! : null;
  const actualL100km = actualKmPerL != null ? (1 / actualKmPerL) * 100 : null;

  if (!hasBenchmark) {
    return {
      pct: null,
      kmPerL: actualKmPerL,
      l100km: actualL100km,
      confidence: 'no_benchmark',
      confidenceLabel: 'No benchmark set',
      tooltipNote: 'Set a km/L benchmark on the vehicle to enable efficiency scoring.',
    };
  }

  if (hasActualLitres && actualKmPerL != null) {
    const l100km = actualL100km!;
    const pct = efficiencyScorePercent({
      totalKm30: input.totalKm30,
      refuelCount30: input.refuelCount30,
      needsRefuelCount: 0,
      totalLitresAdded: input.totalLitresAdded,
      tankCapacityL: input.tankCapacityL,
      benchmarkKmPerL: benchmark,
    });
    return {
      pct,
      kmPerL: actualKmPerL,
      l100km,
      confidence: 'measured',
      confidenceLabel: 'Measured',
      tooltipNote: `${input.totalLitresAdded!.toFixed(1)} L · ${input.totalKm30.toFixed(0)} km`,
    };
  }

  if (input.totalKm30 > 0 && input.refuelCount30 >= 1) {
    const tank = input.tankCapacityL || DEFAULT_TANK_CAPACITY_L;
    const estimatedLitres = input.refuelCount30 * tank * FILL_FRACTION;
    const kmPerL = input.totalKm30 / estimatedLitres;
    const l100km = (estimatedLitres / input.totalKm30) * 100;
    const pct = efficiencyScorePercent({
      totalKm30: input.totalKm30,
      refuelCount30: input.refuelCount30,
      needsRefuelCount: 0,
      tankCapacityL: tank,
      benchmarkKmPerL: benchmark,
    });
    return {
      pct,
      kmPerL,
      l100km,
      confidence: 'estimated',
      confidenceLabel: 'Estimated',
      tooltipNote: `Estimated from ${input.refuelCount30} refuel(s), ${tank} L tank`,
    };
  }

  return {
    pct: null,
    kmPerL: null,
    l100km: null,
    confidence: 'early',
    confidenceLabel: 'Need refuel data',
    tooltipNote: `Benchmark ${benchmark} km/L — log refuel events for a score.`,
  };
}

export function ratingFromPct(pct: number | null): string {
  if (pct == null) return '—';
  if (pct >= 85) return 'Excellent';
  if (pct >= 70) return 'Good';
  return 'Fair';
}

export function ratingColorFromLabel(r: string): string {
  if (r === 'Excellent') return '#0F8F4F';
  if (r === 'Good') return '#009EBE';
  if (r === 'Fair') return '#C77A12';
  return '#FF4D67';
}

export function vehicleBenchmarkKmPerL(data: Record<string, unknown>): number | undefined {
  const b = data.benchmarkKmPerLiter ?? data.benchmarkKmPerL;
  return typeof b === 'number' ? b : undefined;
}

export function vehicleTankL(data: Record<string, unknown>): number {
  const t = data.fuelTankCapacityL;
  return typeof t === 'number' ? t : DEFAULT_TANK_CAPACITY_L;
}
